import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { getMe, login, type Me } from "./lib/galleryApi.ts";
import ThemeToggle from "./components/ThemeToggle.tsx";
import SystemNotice from "./components/SystemNotice.tsx";

/*
 * PublicApp — the WEBMASTER-facing site, served by the intake box. Gated by magic-link sign-in:
 * not signed in → a sign-in screen; signed in → the model gallery + profile in `pub` mode
 * (browse + request renders + view thumbnails; no owner actions).
 */

const ModelGallery = lazy(() => import("./pages/ModelGallery.tsx"));
const ModelProfile = lazy(() => import("./pages/ModelProfile.tsx"));
const TeamDashboard = lazy(() => import("./pages/TeamDashboard.tsx"));
const AdminPage = lazy(() => import("./pages/AdminPage.tsx"));
const ShootsByDate = lazy(() => import("./pages/ShootsByDate.tsx"));

function Loading() {
  return (
    <main>
      <p className="muted">Loading…</p>
    </main>
  );
}

function SignIn({ onSignedIn }: { onSignedIn: (me: Me) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  function submit() {
    if (!email.includes("@")) return setErr("enter a valid email");
    if (!password) return setErr("enter the site password");
    setBusy(true);
    setErr(null);
    login(email.trim(), password)
      .then((r) => onSignedIn({ email: r.email }))
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setBusy(false));
  }
  return (
    <main style={{ maxWidth: 420 }}>
      <div style={{ display: "flex", alignItems: "center" }}>
        <h1 style={{ margin: 0 }}>Sign in</h1>
        <div style={{ flex: 1 }} />
        <ThemeToggle />
      </div>
      <p className="muted">Enter your email and the site password (from the editor).</p>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 300 }}>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" autoFocus />
        <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="site password" />
        <button disabled={busy} onClick={submit}>{busy ? "Signing in…" : "Sign in"}</button>
      </div>
      {err && <p style={{ color: "#ef4444" }}>{err}</p>}
    </main>
  );
}

export default function PublicApp() {
  const [me, setMe] = useState<Me | null | undefined>(undefined); // undefined = checking

  useEffect(() => {
    getMe().then(setMe).catch(() => setMe(null));
  }, []);

  // The webmaster gallery is gated by sign-in; /admin has its OWN (separate) admin login, so it
  // stays reachable even without a webmaster session.
  // Every signed-in page carries the outage bar: an off machine must never look like silence.
  const gate = (node: ReactNode) =>
    me === undefined ? (
      <Loading />
    ) : me === null ? (
      <SignIn onSignedIn={setMe} />
    ) : (
      <>
        <SystemNotice />
        {node}
      </>
    );

  return (
    <BrowserRouter>
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/" element={gate(<ModelGallery pub />)} />
          <Route path="/models" element={gate(<ModelGallery pub />)} />
          <Route path="/models/:name" element={gate(<ModelProfile pub />)} />
          <Route path="/shoots" element={gate(<ShootsByDate />)} />
          <Route path="/activity" element={gate(<TeamDashboard />)} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
